/**
 * Copyright © 2025-2026 Delicious AI, LLC
 */

import { matchPath } from 'react-router-dom'

import { SidebarItemsType } from '@/types/sidebar'

import SidebarNavList from './SidebarNavList'
import SidebarNavListItem from './SidebarNavListItem'

type ReduceChildRoutesProps = {
  depth: number
  page: SidebarItemsType
  items: JSX.Element[]
  currentRoute: string
  collapsed: boolean
}

const reduceChildRoutes = (props: ReduceChildRoutesProps) => {
  const { items, page, depth, currentRoute, collapsed } = props

  if (collapsed && depth === 0 && page.children) {
    page.children.forEach(child => {
      items.push(
        <SidebarNavListItem
          depth={depth}
          id={child.id}
          href={child.href}
          icon={child.icon || (() => null)}
          key={child.title}
          badge={child.badge}
          title={child.title}
          collapsed={collapsed}
        />,
      )
    })
    return items
  }

  if (page.children) {
    const childMatch = page.children.some(child =>
      matchPath(
        {
          path: child.href,
          end: false,
        },
        currentRoute,
      ),
    )
    const open = page.href
      ? !!matchPath(
          {
            path: page.href,
            end: true,
          },
          currentRoute,
        )
      : childMatch
    items.push(
      <SidebarNavListItem
        depth={depth}
        icon={page.icon || (() => null)}
        key={page.title}
        id={page.id}
        badge={page.badge}
        open={!!open}
        title={page.title}
        href={page.href}
        collapsed={collapsed}
      >
        {!collapsed && (
          <SidebarNavList
            depth={depth + 1}
            pages={page.children}
            collapsed={collapsed}
          />
        )}
      </SidebarNavListItem>,
    )
  } else {
    items.push(
      <SidebarNavListItem
        depth={depth}
        id={page.id}
        href={page.href}
        icon={page.icon || (() => null)}
        key={page.title}
        badge={page.badge}
        title={page.title}
        collapsed={collapsed}
      />,
    )
  }
  return items
}

export default reduceChildRoutes
